import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Ban, CheckCircle2, FileUp, Loader2, Search, Split, X } from 'lucide-react';
import { Customer, Invoice, InvoicePayment } from '../../types';
import { Button } from '../Button';
import { BankTransaction, parseCamt053 } from '../../services/bank/camt053';
import { MergedStatements, StatementFile, mergeStatements } from '../../services/bank/mergeStatements';
import { MatchConfidence, PaymentMatch, matchPayments } from '../../services/bank/matchPayments';
import {
    PaymentIndex,
    SplitDraft,
    SplitEvaluation,
    buildPaymentIndex,
    doneReasons,
    emptyPaymentIndex,
    evaluateSplit,
    initialSplitDrafts,
} from '../../services/bank/importPlan';
import { fetchInvoices } from '../../services/invoiceService';
import {
    addPayment,
    addPaymentWithRemainder,
    addSplitPayment,
    fetchPaymentsForInvoices,
    findPaymentsForBankTx,
} from '../../services/invoicePayments';
import {
    checkRemainder,
    paymentRemainder,
    REMAINDER_LABEL,
    RemainderKind,
} from '../../services/paymentRemainder';
import { PaymentDraft, openAmountCents, validatePayment } from '../../services/invoiceLogic';
import { centsFromEuroInput, formatCents, formatCentsPlain } from '../../services/money';
import { RemainderOption } from './RemainderOption';
import { InvoiceSearchPopover } from './InvoiceSearchPopover';
import { SplitEditor } from './SplitEditor';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';

/**
 * Zahlungsimport aus einem CAMT.053-Kontoauszug oder einer
 * CAMT.052-Umsatzinformation (M8).
 *
 * Die Oberflaeche schlaegt vor und bucht erst auf ausdruecklichen Klick. Das ist
 * die Fortsetzung der Entscheidung aus matchPayments: eine falsch zugeordnete
 * Zahlung setzt eine Rechnung auf bezahlt, die es nicht ist, und laesst
 * gleichzeitig eine andere in die Mahnung laufen. Beides faellt nur dem auf, der
 * den Auszug ohnehin vor sich hat — also genau hier.
 *
 * Je Umsatz: eine Rechnung (Vorschlag, Auswahl oder Handsuche) mit Wahl, was
 * mit einem Rest passiert (offen lassen, Skonto, Sicherheitseinbehalt) — oder
 * eine Aufteilung auf mehrere Rechnungen (Sammelzahlung). Bereits erfasste
 * Umsaetze (auch von Hand gebuchte, src/services/bank/importPlan.ts) sind
 * ausgeblendet und lassen sich einblenden.
 *
 * Der Dialog laedt die Rechnungen selbst und nicht aus dem Aufrufer: die
 * Zuordnung haengt an den offenen Betraegen, und eine Liste, die seit dem
 * Oeffnen der Ansicht im Speicher liegt, kann sie nicht mehr richtig wiedergeben.
 */

type BookingStatus = 'booking' | 'booked' | 'duplicate' | 'conflict' | 'failed';

interface BookingResult {
    status: BookingStatus;
    message: string;
}

interface RowView {
    /**
     * Eindeutiger Schluessel DIESER Zeile.
     *
     * Nicht die bankTxId: die stammt bei vorhandener AcctSvcrRef unveraendert
     * von der Bank, und eine Stornobuchung traegt in der Praxis dieselbe
     * Referenz wie die Buchung, die sie storniert. Zwei Zeilen teilten sich
     * dann Auswahl und Ergebnisanzeige — eine gebuchte Zahlung erschiene an
     * der falschen Zeile, oder eine gesperrte Zeile meldete "gebucht".
     */
    rowKey: string;
    match: PaymentMatch;
    transaction: BankTransaction;
    /** Die aktuell gewaehlte Zielrechnung — Vorschlag oder Auswahl des Nutzers. */
    invoice: Invoice | null;
    /** Vorschlag plus gleich gute Kandidaten, ohne Doppelungen. */
    options: Invoice[];
    /** Warum der Umsatz als bereits erfasst gilt (ausblendbar) — oder null. */
    done: string | null;
    /** Aufteilung auf mehrere Rechnungen, wenn gewaehlt. */
    split: SplitDraft[] | null;
    splitEval: SplitEvaluation | null;
    /** Was mit dem Rest passiert (nur ohne Aufteilung). */
    remainder: RemainderKind | null;
    /** Grund, warum die Zeile nicht buchbar ist. Null heisst: buchbar. */
    blocked: string | null;
    /** Hinweise, die nicht blockieren (Ueberzahlung, Datum vor Rechnungsdatum). */
    warnings: string[];
    result?: BookingResult;
}

interface SearchTarget {
    rowKey: string;
    /** Gesetzt, wenn fuer einen Teil einer Aufteilung gesucht wird. */
    partKey?: string;
}

const CONFIDENCE_LABEL: Record<MatchConfidence, string> = {
    high: 'hoch',
    medium: 'mittel',
    low: 'gering',
};

const CONFIDENCE_CHIP: Record<MatchConfidence, string> = {
    high: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    medium: 'border-amber-200 bg-amber-50 text-amber-800',
    low: 'border-slate-300 bg-slate-100 text-slate-600',
};

const de = (iso?: string): string => (iso ? iso.split('-').reverse().join('.') : '—');

const invoiceLabel = (invoice: Invoice): string =>
    `${invoice.invoiceNumber ? `Rechnung ${invoice.invoiceNumber}` : 'Rechnung ohne Nummer'}${
        invoice.customerName ? ` · ${invoice.customerName}` : ''
    }`;

/** Mehrzeiliger Text wird nach drei Zeilen gekuerzt, der volle Text steht im Tooltip. */
const CLAMP_3: React.CSSProperties = {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    overflowWrap: 'anywhere',
};

/**
 * Die Buchung, die aus einem Umsatz entstehen wuerde.
 *
 * Immer INCOMING und immer der volle Umsatzbetrag — auf mehrere Rechnungen
 * verteilt nur ueber die ausdrueckliche Aufteilung.
 */
const paymentDraft = (transaction: BankTransaction): PaymentDraft => ({
    direction: 'INCOMING',
    valueDate: transaction.valueDate,
    amountCents: transaction.amountCents,
    method: 'BANK_TRANSFER',
    reference: transaction.remittanceInfo || undefined,
    note: transaction.counterpartyName ? `Zahler: ${transaction.counterpartyName}` : undefined,
});

/** Zeichensatz aus der XML-Deklaration, sofern die Datei einen nennt. */
const declaredEncoding = (text: string): string =>
    /^<\?xml[^>]*\bencoding\s*=\s*["']([^"']+)["']/i.exec(text.slice(0, 256))?.[1] ?? '';

const readFileText = (file: File, encoding: string): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () =>
            reject(new Error(`Die Datei "${file.name}" konnte nicht gelesen werden.`));
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
        reader.readAsText(file, encoding);
    });

/**
 * Text einer Auszugsdatei im richtigen Zeichensatz.
 *
 * Der Auszug kommt je nach Ausgabekanal als UTF-8 oder als ISO-8859-1. Wird
 * die Deklaration uebergangen, verlieren Zahlername und Verwendungszweck ihre
 * Umlaute — also genau die beiden Felder, ueber die die Zuordnung laeuft.
 */
const readStatementText = async (file: File): Promise<string> => {
    const text = await readFileText(file, 'UTF-8');
    const encoding = declaredEncoding(text);
    return encoding && !/^utf-?8$/i.test(encoding) ? readFileText(file, encoding) : text;
};

/**
 * Rechnungen je Block abfragen.
 *
 * Nicht alle Ids auf einmal: sie landen als Liste in der Adresszeile der
 * Abfrage, und ein Bestand von einigen hundert Rechnungen sprengte deren
 * Laenge. Ein abgeschnittener Filter faende zu wenige Zahlungen — bereits
 * gebuchte Umsaetze wuerden dann erneut als offen angeboten.
 */
const INVOICE_CHUNK = 100;

const loadPaymentIndex = async (invoices: Invoice[]): Promise<PaymentIndex> => {
    const all: InvoicePayment[] = [];
    for (let start = 0; start < invoices.length; start += INVOICE_CHUNK) {
        const chunk = invoices.slice(start, start + INVOICE_CHUNK).map((invoice) => invoice.id);
        const payments = await fetchPaymentsForInvoices(chunk);
        payments.forEach((list) => all.push(...list));
    }
    return buildPaymentIndex(all);
};

/**
 * Warum der Umsatz selbst nicht buchbar ist — unabhaengig von der Rechnung.
 *
 * Der Grund wird zurueckgegeben statt die Zeile stumm auszublenden: ein
 * Ausgang oder ein vorgemerkter Umsatz, der einfach fehlt, laesst den
 * Anwender glauben, der Auszug sei unvollstaendig eingelesen worden.
 */
const transactionBlockingReason = (
    transaction: BankTransaction,
    alreadyImported: boolean,
): string | null => {
    if (alreadyImported) {
        return 'Dieser Umsatz ist bereits als Zahlung erfasst.';
    }
    if (!transaction.booked) {
        return 'Nur vorgemerkt — der Umsatz ist noch nicht endgültig gebucht und kann sich noch ändern.';
    }
    if (transaction.amountCents < 0) {
        return 'Zahlungsausgang (Lastschrift, Erstattung oder Gebühr) — kein Zahlungseingang.';
    }
    if (transaction.amountCents === 0) {
        return 'Der Umsatz hat keinen Betrag.';
    }
    if (transaction.currency && transaction.currency.toUpperCase() !== 'EUR') {
        return `Fremdwährung ${transaction.currency} — es können nur Euro-Umsätze gebucht werden.`;
    }
    return null;
};

/** Warum die Buchung auf DIESE Rechnung nicht geht — oder null. */
const invoiceBlockingReason = (
    transaction: BankTransaction,
    invoice: Invoice | null,
    todayIso: string,
): string | null => {
    if (!invoice) {
        return 'Keine Rechnung zugeordnet. Bitte eine auswählen, aufteilen oder die Zahlung von Hand erfassen.';
    }
    // Mehrere Steuersaetze: addPayment kann die Aufteilung in Netto und
    // Umsatzsteuer dann nicht rechnen, und der Kontoauszug enthaelt sie nicht.
    // Geraten wird sie nicht — Umsatzsteuervoranmeldung und DATEV-Export
    // haengen daran.
    if (invoice.totals.taxGroups.filter((group) => group.taxableNetCents !== 0).length > 1) {
        return 'Die Rechnung enthält mehrere Steuersätze. Die Aufteilung in Netto und Umsatzsteuer muss an der Rechnung selbst erfasst werden.';
    }
    const blocking = validatePayment(paymentDraft(transaction), invoice, todayIso).find(
        (message) => message.level === 'error',
    );
    return blocking?.message ?? null;
};

/** Suchform: Kleinbuchstaben, Umlaute ausgeschrieben, Mehrfach-Leerzeichen zusammengezogen. */
const searchForm = (value: string): string =>
    value
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .replace(/\s+/g, ' ')
        .trim();

/** Wie viele Treffer die Handsuche hoechstens zeigt. */
const SEARCH_LIMIT = 12;

const warningsFor = (
    transaction: BankTransaction,
    invoice: Invoice | null,
    todayIso: string,
): string[] =>
    invoice
        ? validatePayment(paymentDraft(transaction), invoice, todayIso)
              .filter((message) => message.level === 'warning')
              .map((message) => message.message)
        : [];

interface Props {
    /** Heutiges Datum als ISO-String — Bezugspunkt der Zahlungspruefung. */
    today: string;
    onClose: () => void;
    /**
     * Kunden fuer den Namensabgleich. Nur noetig, wenn Invoice.customerName
     * fehlt; der Aufrufer hat die Liste meist ohnehin geladen.
     */
    customers?: Customer[];
    /**
     * Wird nach mindestens einer erfolgreichen Buchung aufgerufen. Der
     * Zahlungsstatus entsteht im Datenbank-Trigger, deshalb muss der Aufrufer
     * seinen Bestand neu laden statt lokal fortzuschreiben.
     */
    onImported?: () => void | Promise<void>;
}

export const BankImportDialog: React.FC<Props> = ({ today, onClose, customers, onImported }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const partSeq = useRef(0);
    const nextPartKey = () => {
        partSeq.current += 1;
        return `t${partSeq.current}`;
    };

    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [paymentIndex, setPaymentIndex] = useState<PaymentIndex>(emptyPaymentIndex);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    const [fileNames, setFileNames] = useState<string[]>([]);
    /** Alle gewaehlten Dateien zusammengefuehrt (Haspa: eine Datei je Tag). */
    const [statement, setStatement] = useState<MergedStatements | null>(null);
    /** Je nicht eingelesener Datei eine Zeile — oder der Grund, warum die Auswahl abgelehnt wurde. */
    const [parseErrors, setParseErrors] = useState<string[]>([]);
    const [reading, setReading] = useState(false);

    const [choice, setChoice] = useState<Record<string, string>>({});
    const [selected, setSelected] = useState<Record<string, boolean>>({});
    const [results, setResults] = useState<Record<string, BookingResult>>({});
    const [booking, setBooking] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });

    /** Was mit dem Rest je Zeile passiert — nie vorbelegt. */
    const [remainder, setRemainder] = useState<Record<string, RemainderKind | null>>({});
    /** Aufteilungen auf mehrere Rechnungen je Zeile. */
    const [split, setSplit] = useState<Record<string, SplitDraft[]>>({});
    /** Bereits erfasste Umsaetze (auch von Hand gebuchte) ausblenden. */
    const [hideDone, setHideDone] = useState(true);
    const [hideOutgoing, setHideOutgoing] = useState(false);

    /** Zeile (und ggf. Teil), fuer die gerade von Hand eine Rechnung gesucht wird. */
    const [search, setSearch] = useState<SearchTarget | null>(null);
    const [searchText, setSearchText] = useState('');

    /**
     * Rechnungen und bereits erfasste Zahlungen laden. Wirft weiter.
     *
     * Bewusst ohne eigenes catch: nach dem Buchen muss der Aufrufer
     * unterscheiden koennen zwischen "die Buchung ist gescheitert" und "die
     * Buchung lief, aber die Anzeige ist nicht mehr aktuell".
     */
    const load = useCallback(async () => {
        setLoading(true);
        try {
            // Ohne Positionen: der Abgleich liest nur Kopfdaten — Nummer,
            // Kunde, Status, Summen und den offenen Betrag (matchPayments,
            // validatePayment, addPayment ueber totals.taxGroups). Die Zeilen
            // waeren hier reine Ladezeit.
            const list = await fetchInvoices({ withoutLines: true });
            setInvoices(list);
            setPaymentIndex(await loadPaymentIndex(list));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load().catch((e) =>
            setError(e instanceof Error ? e.message : 'Die Rechnungen konnten nicht geladen werden.'),
        );
    }, [load]);

    const invoicesById = useMemo(
        () => new Map(invoices.map((invoice) => [invoice.id, invoice])),
        [invoices],
    );

    const matches = useMemo(
        () =>
            statement
                ? matchPayments(statement.transactions, invoices, {
                      customers,
                      alreadyImportedBankTxIds: Array.from(paymentIndex.importedBankTxIds),
                  })
                : [],
        [statement, invoices, customers, paymentIndex],
    );

    /** Je Umsatz der Grund, warum er als erfasst gilt — Handzahlungen verbrauchen sich. */
    const done = useMemo(() => doneReasons(matches, paymentIndex), [matches, paymentIndex]);

    const rows: RowView[] = useMemo(
        () =>
            matches.map((match, index) => {
                const transaction = match.transaction;
                const rowKey = `${transaction.bankTxId}#${index}`;
                const chosenId = choice[rowKey];
                const invoice =
                    chosenId === undefined
                        ? match.invoice
                        : (invoicesById.get(chosenId) ?? null);

                const options: Invoice[] = [];
                for (const candidate of [
                    ...(match.invoice ? [match.invoice] : []),
                    ...match.candidates,
                    ...(invoice ? [invoice] : []),
                ]) {
                    if (!options.some((known) => known.id === candidate.id)) options.push(candidate);
                }

                const imported = paymentIndex.importedBankTxIds.has(transaction.bankTxId);
                const txBlocked = transactionBlockingReason(transaction, imported);
                const drafts = split[rowKey] ?? null;
                const splitEval = drafts ? evaluateSplit(transaction, drafts, invoicesById, today) : null;
                const rowRemainder = drafts ? null : (remainder[rowKey] ?? null);

                let blocked: string | null = txBlocked;
                if (!blocked && splitEval) {
                    blocked = splitEval.parts ? null : 'Aufteilung noch nicht vollständig — siehe darunter.';
                }
                if (!blocked && !splitEval) {
                    blocked = invoiceBlockingReason(transaction, invoice, today);
                    if (!blocked && invoice && rowRemainder) {
                        const rest = paymentRemainder(invoice, transaction.amountCents);
                        const remainderError = checkRemainder(
                            invoice,
                            rowRemainder,
                            rest,
                            transaction.valueDate,
                            transaction.amountCents,
                        ).find((message) => message.level === 'error');
                        if (remainderError) blocked = remainderError.message;
                    }
                }

                return {
                    rowKey,
                    match,
                    transaction,
                    invoice,
                    options,
                    done: done[index],
                    split: drafts,
                    splitEval,
                    remainder: rowRemainder,
                    blocked,
                    warnings: splitEval ? [] : warningsFor(transaction, invoice, today),
                    result: results[rowKey],
                };
            }),
        [matches, choice, invoicesById, paymentIndex, results, today, split, remainder, done],
    );

    /** Ein in dieser Sitzung gebuchter Umsatz bleibt sichtbar, samt Meldung. */
    const handledNow = (row: RowView) =>
        row.result?.status === 'booked' || row.result?.status === 'duplicate';

    const visibleRows = useMemo(
        () =>
            rows.filter(
                (row) =>
                    handledNow(row) ||
                    (!(hideDone && row.done) && !(hideOutgoing && row.transaction.amountCents < 0)),
            ),
        [rows, hideDone, hideOutgoing],
    );
    const hiddenDone = rows.filter((row) => row.done && !handledNow(row)).length;
    const hiddenOutgoing = rows.filter((row) => row.transaction.amountCents < 0 && !row.done).length;

    /** Buchbar heisst: sichtbar, nicht gesperrt, Ziel gewaehlt, noch nicht gebucht. */
    const bookable = useMemo(
        () =>
            visibleRows.filter(
                (row) =>
                    !row.blocked &&
                    (row.splitEval ? !!row.splitEval.parts : !!row.invoice) &&
                    !handledNow(row),
            ),
        [visibleRows],
    );

    const chosen = useMemo(
        () => bookable.filter((row) => selected[row.rowKey]),
        [bookable, selected],
    );

    const customerNames = useMemo(
        () => new Map((customers ?? []).map((customer) => [customer.id, customer.name])),
        [customers],
    );
    const customerNameOf = useCallback(
        (invoice: Invoice): string =>
            invoice.customerName || customerNames.get(invoice.customerId) || '',
        [customerNames],
    );

    /**
     * Treffer der Handsuche.
     *
     * Angeboten wird nur, worauf die Zahlung auch gebucht werden koennte:
     * offener Betrag und keine sperrende Pruefung (Entwurf, storniert). Ein
     * Treffer, der beim Buchen sofort abgewiesen wird, waere eine falsche Spur.
     * Rechnungen mit genau dem gesuchten Betrag (Umsatz oder Teilbetrag) stehen
     * oben.
     */
    const searchResults = useMemo(() => {
        const row = search ? rows.find((candidate) => candidate.rowKey === search.rowKey) : null;
        if (!row || !search) return [];
        const part = search.partKey ? row.split?.find((draft) => draft.key === search.partKey) : undefined;
        const targetCents =
            (part ? centsFromEuroInput(part.amountInput) : null) ?? row.transaction.amountCents;
        const needle = searchForm(searchText);
        const draft = { ...paymentDraft(row.transaction), amountCents: targetCents };
        return invoices
            .filter((invoice) => {
                const open = openAmountCents(invoice);
                if (open <= 0) return false;
                if (validatePayment(draft, invoice, today).some((m) => m.level === 'error')) {
                    return false;
                }
                if (!needle) return true;
                const number = invoice.invoiceNumber ?? '';
                const haystack = searchForm(
                    [
                        number,
                        number.replace(/[^A-Za-z0-9]/g, ''),
                        customerNameOf(invoice),
                        formatCents(open),
                        (open / 100).toFixed(2).replace('.', ','),
                    ].join(' '),
                );
                return needle.split(' ').every((token) => haystack.includes(token));
            })
            .sort((a, b) => {
                const exactA = openAmountCents(a) === targetCents ? 0 : 1;
                const exactB = openAmountCents(b) === targetCents ? 0 : 1;
                if (exactA !== exactB) return exactA - exactB;
                return (b.invoiceNumber ?? '').localeCompare(a.invoiceNumber ?? '', 'de', {
                    numeric: true,
                });
            })
            .slice(0, SEARCH_LIMIT);
    }, [search, searchText, rows, invoices, today, customerNameOf]);

    const openSearch = (target: SearchTarget) => {
        setSearch(target);
        setSearchText('');
    };
    const closeSearch = () => {
        setSearch(null);
        setSearchText('');
    };

    /**
     * Rechnung einer Zeile wechseln — per Auswahlliste oder Handsuche.
     *
     * Haken und Restwahl fallen dabei weg: sie galten der vorher gewaehlten
     * Rechnung, und jede Zuordnung soll einzeln bestaetigt sein.
     */
    const chooseInvoice = (rowKey: string, invoiceId: string) => {
        setChoice((prev) => ({ ...prev, [rowKey]: invoiceId }));
        setSelected((prev) => ({ ...prev, [rowKey]: false }));
        setRemainder((prev) => ({ ...prev, [rowKey]: null }));
    };

    const setSplitDrafts = (rowKey: string, drafts: SplitDraft[]) => {
        setSplit((prev) => ({ ...prev, [rowKey]: drafts }));
        // Jede Aenderung an der Aufteilung verlangt eine neue Bestaetigung.
        setSelected((prev) => ({ ...prev, [rowKey]: false }));
    };

    const startSplit = (row: RowView) => {
        const suggested = [...(row.invoice ? [row.invoice] : []), ...row.match.candidates].filter(
            (invoice) => openAmountCents(invoice) > 0,
        );
        setSplitDrafts(
            row.rowKey,
            initialSplitDrafts(row.transaction, suggested, nextPartKey, formatCentsPlain),
        );
        setRemainder((prev) => ({ ...prev, [row.rowKey]: null }));
    };

    const cancelSplit = (rowKey: string) => {
        setSplit((prev) => {
            const next = { ...prev };
            delete next[rowKey];
            return next;
        });
        setSelected((prev) => ({ ...prev, [rowKey]: false }));
        if (search?.rowKey === rowKey) closeSearch();
    };

    const addSplitPart = (row: RowView) => {
        const drafts = row.split ?? [];
        const rest = row.splitEval?.restCents ?? 0;
        setSplitDrafts(row.rowKey, [
            ...drafts,
            {
                key: nextPartKey(),
                invoiceId: '',
                amountInput: rest > 0 ? formatCentsPlain(rest) : '',
                remainder: null,
            },
        ]);
    };

    const pickFromSearch = (invoiceId: string) => {
        if (!search) return;
        const { rowKey, partKey } = search;
        if (partKey) {
            const row = rows.find((candidate) => candidate.rowKey === rowKey);
            const drafts = row?.split ?? [];
            const invoice = invoicesById.get(invoiceId);
            const rest = row?.splitEval?.restCents ?? 0;
            setSplitDrafts(
                rowKey,
                drafts.map((draft) => {
                    if (draft.key !== partKey) return draft;
                    // Ohne Betrag vorbelegen: offener Betrag der Rechnung, hoechstens der noch zu verteilende Rest.
                    const fill =
                        !draft.amountInput.trim() && invoice && rest > 0
                            ? formatCentsPlain(Math.min(openAmountCents(invoice), rest))
                            : draft.amountInput;
                    return { ...draft, invoiceId, amountInput: fill, remainder: null };
                }),
            );
        } else {
            chooseInvoice(rowKey, invoiceId);
        }
        closeSearch();
    };

    const resetImport = (names: string[]) => {
        setStatement(null);
        setParseErrors([]);
        setNotice(null);
        setError(null);
        setChoice({});
        setSelected({});
        setRemainder({});
        setSplit({});
        setResults({});
        setProgress({ done: 0, total: 0 });
        closeSearch();
        setFileNames(names);
    };

    /**
     * Eine oder mehrere Auszugsdateien einlesen.
     *
     * Eine Datei, die nicht gelesen werden kann, wird mit ihrem Grund
     * aufgefuehrt und fehlt in der Tabelle; die uebrigen werden trotzdem
     * angezeigt. Dieser Fehler muss stehen bleiben: eine fehlende Datei liesse
     * sich sonst als "an diesem Tag sind keine Zahlungen eingegangen" lesen.
     * Mischt die Auswahl Konten oder Formate, wird sie ganz abgelehnt
     * (mergeStatements).
     */
    const handleFiles = async (files: File[]) => {
        resetImport(files.map((file) => file.name));
        setReading(true);
        const parsed: StatementFile[] = [];
        const failures: string[] = [];
        try {
            for (const file of files) {
                try {
                    parsed.push({ name: file.name, statement: parseCamt053(await readStatementText(file)) });
                } catch (e) {
                    failures.push(
                        `${file.name}: ${e instanceof Error ? e.message : 'Die Datei konnte nicht gelesen werden.'}`,
                    );
                }
            }
            if (parsed.length) {
                try {
                    setStatement(mergeStatements(parsed));
                } catch (e) {
                    failures.push(
                        e instanceof Error ? e.message : 'Die Dateien konnten nicht zusammengeführt werden.',
                    );
                }
            }
            setParseErrors(failures);
        } finally {
            setReading(false);
        }
    };

    const selectHighConfidence = () => {
        const next: Record<string, boolean> = {};
        for (const row of bookable) {
            // Aufteilungen sind immer eine ausdrueckliche Einzelentscheidung.
            if (!row.split && row.match.confidence === 'high') next[row.rowKey] = true;
        }
        setSelected(next);
    };

    /** Was schon zu diesem Umsatz in der Datenbank steht — in Worten. */
    const describeExisting = (existing: InvoicePayment[]): string =>
        existing
            .map((payment) => {
                const target = invoicesById.get(payment.invoiceId);
                return `${target ? invoiceLabel(target) : 'eine andere Rechnung'} (${formatCents(
                    payment.amountCents,
                )} vom ${de(payment.valueDate)})`;
            })
            .join(', ');

    const handleBook = async () => {
        const queue = chosen;
        if (!queue.length) return;
        closeSearch();
        setBooking(true);
        setError(null);
        setNotice(null);
        setProgress({ done: 0, total: queue.length });

        let booked = 0;
        // Sequenziell und bewusst nicht mit Promise.all: jede Buchung loest den
        // Trigger aus, der paid_cents und den Zahlungsstatus der Rechnung neu
        // berechnet. Mehrere gleichzeitige Buchungen auf dieselbe Rechnung
        // liefen gegen denselben Ausgangsstand — und die Datenbank bekaeme
        // ausserdem eine ganze Datei auf einen Schlag.
        for (const row of queue) {
            const transaction = row.transaction;
            // Zustandsschluessel ist die Zeile, der Datenbankschluessel bleibt
            // die bankTxId.
            const key = row.rowKey;
            const bankTxId = transaction.bankTxId;
            setResults((prev) => ({
                ...prev,
                [key]: { status: 'booking', message: 'Wird gebucht …' },
            }));

            const classify = (existing: InvoicePayment[]): BookingResult => {
                const sameTarget =
                    !row.split && !!row.invoice && existing.every((p) => p.invoiceId === row.invoice?.id);
                return sameTarget
                    ? {
                          status: 'duplicate',
                          message: `War bereits gebucht (${describeExisting(existing)}). Es wurde nichts zusätzlich angelegt.`,
                      }
                    : {
                          status: 'conflict',
                          message: `Dieser Umsatz ist bereits gebucht: ${describeExisting(
                              existing,
                          )}. Hier wurde nichts gebucht — bitte klären, welche Zuordnung stimmt.`,
                      };
            };

            try {
                // Vor dem Schreiben nachsehen, statt die Kollision abzuwarten:
                // nur so laesst sich die harmlose Wiederholung von der
                // Fehlzuordnung unterscheiden. Aufgeteilte Zahlungen tragen
                // Teilschluessel — findPaymentsForBankTx findet beide Formen.
                const existing = await findPaymentsForBankTx(bankTxId);
                if (existing.length) {
                    const result = classify(existing);
                    setResults((prev) => ({ ...prev, [key]: result }));
                } else if (row.splitEval?.parts) {
                    const parts = row.splitEval.parts;
                    await addSplitPayment(
                        { ...paymentDraft(transaction), bankTxId },
                        transaction.amountCents,
                        parts,
                        today,
                    );
                    booked += 1;
                    setResults((prev) => ({
                        ...prev,
                        [key]: {
                            status: 'booked',
                            message: `Aufgeteilt: ${parts
                                .map(
                                    (part) =>
                                        `${part.invoice.invoiceNumber ?? 'ohne Nummer'} ${formatCents(part.amountCents)}${
                                            part.remainder
                                                ? ` + ${REMAINDER_LABEL[part.remainder.kind]} ${formatCents(
                                                      part.remainder.amountCents,
                                                  )}`
                                                : ''
                                        }`,
                                )
                                .join(', ')}.`,
                        },
                    }));
                } else {
                    const invoice = row.invoice as Invoice;
                    const input = {
                        ...paymentDraft(transaction),
                        invoiceId: invoice.id,
                        // Der Idempotenzschluessel der Datenbank ist die
                        // bankTxId, nicht der Zeilenschluessel der Anzeige.
                        bankTxId,
                    };
                    const kind = row.remainder;
                    const rest = kind ? paymentRemainder(invoice, transaction.amountCents) : 0;
                    // Mit Skonto oder Einbehalt: Zahlung und Rest in EINER
                    // Anweisung — beide oder keine.
                    if (kind && rest > 0) {
                        await addPaymentWithRemainder(input, { kind, amountCents: rest }, invoice, today);
                    } else {
                        await addPayment(input, invoice, today);
                    }
                    booked += 1;
                    setResults((prev) => ({
                        ...prev,
                        [key]: {
                            status: 'booked',
                            message:
                                kind && rest > 0
                                    ? `Gebucht auf ${invoiceLabel(invoice)}, ${REMAINDER_LABEL[kind]} ${formatCents(rest)}.`
                                    : `Gebucht auf ${invoiceLabel(invoice)}.`,
                        },
                    }));
                }
            } catch (e) {
                // Rechtefehler der Datenbank (Nr. 151) im Klartext, sonst wie bisher.
                const fallback = rechtFehlerOder(e, () =>
                    e instanceof Error ? e.message : 'Die Zahlung konnte nicht gebucht werden.',
                );
                let result: BookingResult = { status: 'failed', message: fallback };
                try {
                    // Zwischen Nachsehen und Schreiben kann eine andere Sitzung
                    // denselben Umsatz gebucht haben. Was tatsaechlich in der
                    // Datenbank steht, entscheidet die Einordnung — nicht der
                    // Wortlaut der Fehlermeldung.
                    const clash = await findPaymentsForBankTx(bankTxId);
                    if (clash.length) result = classify(clash);
                } catch {
                    // Die Nachfrage dient nur der Einordnung. Scheitert sie,
                    // bleibt die urspruengliche Meldung stehen.
                    result = { status: 'failed', message: fallback };
                }
                setResults((prev) => ({ ...prev, [key]: result }));
            }

            setProgress((prev) => ({ ...prev, done: prev.done + 1 }));
        }

        setBooking(false);
        setSelected({});

        if (booked > 0) {
            setNotice(
                `${booked} ${booked === 1 ? 'Umsatz' : 'Umsätze'} gebucht. Der Zahlungsstatus der Rechnungen wird von der Datenbank fortgeschrieben.`,
            );
            // Ab hier ist das Geld gebucht. Scheitert das Nachladen, ist das
            // eine ANDERE Lage als eine gescheiterte Buchung — und der Nutzer
            // darf auf keinen Fall ein zweites Mal buchen.
            try {
                await load();
                await onImported?.();
            } catch (e) {
                setError(
                    `Die Zahlungen wurden gebucht, die Ansicht konnte aber nicht aktualisiert werden (${
                        e instanceof Error ? e.message : 'unbekannter Fehler'
                    }). Bitte die Seite neu laden — NICHT erneut buchen.`,
                );
            }
        }
    };

    const busy = reading || booking || loading;

    const searchNodeFor = (target: SearchTarget) =>
        search && search.rowKey === target.rowKey && search.partKey === target.partKey ? (
            <InvoiceSearchPopover
                text={searchText}
                onText={setSearchText}
                results={searchResults}
                onPick={pickFromSearch}
                onClose={closeSearch}
                customerNameOf={customerNameOf}
            />
        ) : null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 animate-fade-in backdrop-blur-sm p-2 sm:p-3">
            <div className="flex max-h-[95vh] w-full max-w-[min(1760px,98vw)] flex-col overflow-hidden rounded-xl bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
                    <h2 className="flex items-center gap-2 font-semibold text-slate-800">
                        <FileUp className="h-5 w-5 text-slate-400" />
                        Zahlungen aus Kontoauszug (CAMT)
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={booking}
                        className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-50"
                        title={booking ? 'Während des Buchens nicht schließen' : 'Schließen'}
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="space-y-2 border-b border-slate-200 px-5 py-3">
                    <div className="flex flex-wrap items-center gap-3">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xml,application/xml,text/xml"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                                const files = Array.from(e.target.files ?? []);
                                // Wert zuruecksetzen, damit dieselbe Datei nach
                                // einer Korrektur erneut gewaehlt werden kann —
                                // ohne das loest ein zweites Mal kein change aus.
                                e.target.value = '';
                                if (files.length) void handleFiles(files);
                            }}
                        />
                        <Button
                            variant="secondary"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={busy}
                        >
                            {reading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Dateien werden gelesen …
                                </>
                            ) : (
                                <>
                                    <FileUp className="mr-2 h-4 w-4" />
                                    Kontoauszüge wählen (.xml, mehrere möglich)
                                </>
                            )}
                        </Button>
                        {fileNames.length === 1 && (
                            <span className="text-xs text-slate-500">{fileNames[0]}</span>
                        )}
                        {fileNames.length > 1 && (
                            <span className="text-xs text-slate-500" title={fileNames.join('\n')}>
                                {fileNames.length} Dateien
                            </span>
                        )}
                        {loading && (
                            <span className="flex items-center gap-1.5 text-xs text-slate-500">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                Rechnungen werden geladen …
                            </span>
                        )}
                    </div>

                    <p className="text-xs text-slate-500">
                        Es wird nichts automatisch gebucht. Jede Zeile muss einzeln bestätigt werden.
                    </p>

                    {statement && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                            <span className="font-medium">
                                {statement.format === 'camt.052'
                                    ? 'Umsatzliste (camt.052)'
                                    : 'Kontoauszug (camt.053)'}
                            </span>
                            {statement.iban && (
                                <span>
                                    Konto <span className="font-medium">{statement.iban}</span>
                                </span>
                            )}
                            {/* Die Umsatzliste traegt keine Auszugsnummer, nur
                                eine feste Kennung des Onlinebankings. */}
                            {statement.format === 'camt.053' &&
                                statement.statementIds.length > 0 &&
                                statement.statementIds.length <= 3 && (
                                    <span>Auszug {statement.statementIds.join(', ')}</span>
                                )}
                            {statement.firstValueDate && statement.lastValueDate && (
                                <span>
                                    {statement.firstValueDate === statement.lastValueDate
                                        ? de(statement.firstValueDate)
                                        : `${de(statement.firstValueDate)} – ${de(statement.lastValueDate)}`}
                                </span>
                            )}
                            <span>{statement.transactions.length} Umsätze</span>
                            {statement.duplicatesSkipped > 0 && (
                                <span title="Derselbe Umsatz stand in mehreren Dateien und wird nur einmal gezeigt.">
                                    {statement.duplicatesSkipped} doppelt enthaltene ausgelassen
                                </span>
                            )}
                            {statement.closingBalanceCents !== undefined && (
                                <span>Endsaldo {formatCents(statement.closingBalanceCents)}</span>
                            )}
                        </div>
                    )}

                    {statement && statement.warnings.length > 0 && (
                        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                            <ul className="space-y-0.5">
                                {statement.warnings.map((warning) => (
                                    <li key={warning}>{warning}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {parseErrors.length > 0 && (
                        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                            <div>
                                <span className="font-medium">
                                    {statement
                                        ? 'Nicht eingelesen — aus diesen Dateien wurde nichts übernommen:'
                                        : 'Nicht eingelesen — es wurde nichts importiert:'}
                                </span>
                                <ul className="mt-0.5 space-y-0.5">
                                    {parseErrors.map((failure) => (
                                        <li key={failure}>{failure}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                    {error && (
                        <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                            {error}
                        </div>
                    )}
                    {notice && !error && (
                        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-700">
                            {notice}
                        </div>
                    )}

                    {rows.length > 0 && (
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-600">
                            <label className="flex items-center gap-1.5">
                                <input
                                    id="bank-hide-done"
                                    type="checkbox"
                                    checked={hideDone}
                                    onChange={(e) => setHideDone(e.target.checked)}
                                    className="h-3.5 w-3.5"
                                />
                                Schon erfasste ausblenden ({hiddenDone})
                            </label>
                            <label className="flex items-center gap-1.5">
                                <input
                                    id="bank-hide-outgoing"
                                    type="checkbox"
                                    checked={hideOutgoing}
                                    onChange={(e) => setHideOutgoing(e.target.checked)}
                                    className="h-3.5 w-3.5"
                                />
                                Abbuchungen ausblenden ({hiddenOutgoing})
                            </label>
                            <span className="text-slate-400">
                                „Schon erfasst“: der Umsatz ist gebucht, oder die genannte Rechnung ist
                                ausgeglichen bzw. trägt schon eine von Hand erfasste Zahlung über
                                denselben Betrag.
                            </span>
                        </div>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto">
                    {!statement && parseErrors.length === 0 && (
                        <div className="space-y-2 px-5 py-6 text-sm text-slate-500">
                            <p>
                                Noch keine Datei gewählt. Erwartet wird ein CAMT-Export als XML:
                                die Umsatzliste aus dem Onlinebanking (camt.052) oder der
                                Kontoauszug (camt.053). CSV-Dateien werden nicht gelesen.
                            </p>
                            <p>
                                Die Haspa liefert die Umsatzliste je Tag als eigene Datei. Für
                                einen Zeitraum alle Tagesdateien zusammen auswählen — die letzte
                                allein enthält nur den letzten Tag.
                            </p>
                            <p>
                                Bitte immer bei demselben Format bleiben. Innerhalb eines Formats
                                erkennt der Import bereits gebuchte Umsätze sicher, auch wenn sich
                                die Zeiträume zweier Dateien überschneiden.
                            </p>
                        </div>
                    )}

                    {statement && rows.length === 0 && (
                        <p className="px-5 py-6 text-sm text-slate-500">
                            Die Auszüge wurden gelesen, enthalten aber keine Umsätze.
                        </p>
                    )}

                    {rows.length > 0 && visibleRows.length === 0 && (
                        <p className="px-5 py-6 text-sm text-slate-500">
                            Alle Umsätze sind ausgeblendet — oben die Filter lösen, um sie zu sehen.
                        </p>
                    )}

                    {visibleRows.length > 0 && (
                        <table className="w-full table-fixed text-xs">
                            <colgroup>
                                <col style={{ width: '2.25rem' }} />
                                <col style={{ width: '6.25rem' }} />
                                <col style={{ width: '24%' }} />
                                <col style={{ width: '11%' }} />
                                <col style={{ width: '7.5rem' }} />
                                <col style={{ width: '24%' }} />
                                <col style={{ width: '5.5rem' }} />
                                <col />
                            </colgroup>
                            <thead className="sticky top-0 z-10 bg-slate-50 text-left uppercase text-slate-500">
                                <tr>
                                    <th className="px-2 py-2" />
                                    <th className="px-2 py-2">Datum</th>
                                    <th className="px-2 py-2">Verwendungszweck</th>
                                    <th className="px-2 py-2">Zahler</th>
                                    <th className="px-2 py-2 text-right">Betrag</th>
                                    <th className="px-2 py-2">Rechnung</th>
                                    <th className="px-2 py-2">Konfidenz</th>
                                    <th className="px-2 py-2">Begründung</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 align-top">
                                {visibleRows.map((row) => {
                                    const key = row.rowKey;
                                    const outgoing = row.transaction.amountCents < 0;
                                    const imported = paymentIndex.importedBankTxIds.has(
                                        row.transaction.bankTxId,
                                    );
                                    const handAssignable =
                                        !booking &&
                                        !handledNow(row) &&
                                        !transactionBlockingReason(row.transaction, imported);
                                    const byHand =
                                        choice[key] !== undefined &&
                                        choice[key] !== (row.match.invoice?.id ?? '');
                                    return (
                                        <React.Fragment key={key}>
                                            <tr className={row.blocked && !row.split ? 'bg-slate-50 text-slate-400' : ''}>
                                                <td className="px-2 py-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!selected[key]}
                                                        disabled={!!row.blocked || booking || handledNow(row)}
                                                        onChange={(e) =>
                                                            setSelected((prev) => ({
                                                                ...prev,
                                                                [key]: e.target.checked,
                                                            }))
                                                        }
                                                        title={row.blocked ?? 'Zur Buchung vormerken'}
                                                        className="h-4 w-4"
                                                    />
                                                </td>
                                                <td className="whitespace-nowrap px-2 py-2">
                                                    {de(row.transaction.valueDate)}
                                                    {!row.transaction.booked && (
                                                        <div className="text-[11px] text-amber-700">
                                                            vorgemerkt
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-2 py-2">
                                                    <span style={CLAMP_3} title={row.transaction.remittanceInfo}>
                                                        {row.transaction.remittanceInfo || '—'}
                                                    </span>
                                                    {row.transaction.endToEndId && (
                                                        <span
                                                            className="block truncate text-[11px] text-slate-400"
                                                            title={row.transaction.endToEndId}
                                                        >
                                                            E2E {row.transaction.endToEndId}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-2 py-2">
                                                    <span style={CLAMP_3} title={row.transaction.counterpartyName}>
                                                        {row.transaction.counterpartyName || '—'}
                                                    </span>
                                                </td>
                                                <td
                                                    className={`whitespace-nowrap px-2 py-2 text-right font-medium tabular-nums ${
                                                        outgoing && !row.blocked ? 'text-amber-700' : ''
                                                    }`}
                                                >
                                                    {formatCents(row.transaction.amountCents)}
                                                </td>
                                                <td className="px-2 py-2">
                                                    {row.split ? (
                                                        <span className="flex items-center gap-1 font-medium text-sky-700">
                                                            <Split className="h-3.5 w-3.5" />
                                                            Aufgeteilt auf {row.split.length} Rechnungen (darunter)
                                                        </span>
                                                    ) : (
                                                        <>
                                                            {row.options.length > 0 ? (
                                                                <select
                                                                    value={row.invoice?.id ?? ''}
                                                                    disabled={booking || handledNow(row)}
                                                                    onChange={(e) => chooseInvoice(key, e.target.value)}
                                                                    className="w-full rounded border border-slate-300 bg-white px-1.5 py-1 text-slate-700"
                                                                >
                                                                    <option value="">— keine —</option>
                                                                    {row.options.map((option) => (
                                                                        <option key={option.id} value={option.id}>
                                                                            {option.invoiceNumber ?? 'ohne Nummer'}
                                                                            {' · offen '}
                                                                            {formatCents(openAmountCents(option))}
                                                                            {option.customerName
                                                                                ? ` · ${option.customerName}`
                                                                                : ''}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            ) : (
                                                                <span className="text-slate-400">kein Vorschlag</span>
                                                            )}
                                                            {handAssignable && row.invoice && (
                                                                <RemainderOption
                                                                    compact
                                                                    name={`bank-rest-${key}`}
                                                                    invoice={row.invoice}
                                                                    paymentCents={row.transaction.amountCents}
                                                                    valueDate={row.transaction.valueDate}
                                                                    value={row.remainder}
                                                                    onChange={(value) => {
                                                                        setRemainder((prev) => ({ ...prev, [key]: value }));
                                                                        setSelected((prev) => ({ ...prev, [key]: false }));
                                                                    }}
                                                                />
                                                            )}
                                                            {handAssignable && (
                                                                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                                                                    {!(search?.rowKey === key && !search.partKey) && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openSearch({ rowKey: key })}
                                                                            className="flex items-center gap-1 text-[11px] text-sky-700 hover:underline"
                                                                        >
                                                                            <Search className="h-3 w-3" />
                                                                            {row.invoice ? 'Andere Rechnung suchen …' : 'Rechnung suchen …'}
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => startSplit(row)}
                                                                        className="flex items-center gap-1 text-[11px] text-sky-700 hover:underline"
                                                                    >
                                                                        <Split className="h-3 w-3" />
                                                                        Auf mehrere Rechnungen aufteilen
                                                                    </button>
                                                                </div>
                                                            )}
                                                            {searchNodeFor({ rowKey: key })}
                                                        </>
                                                    )}
                                                </td>
                                                <td className="px-2 py-2">
                                                    <span
                                                        className={`inline-block rounded-full border px-2 py-0.5 ${
                                                            CONFIDENCE_CHIP[row.match.confidence]
                                                        }`}
                                                    >
                                                        {CONFIDENCE_LABEL[row.match.confidence]}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-2" style={{ overflowWrap: 'anywhere' }}>
                                                    <span className="block text-slate-600">{row.match.reason}</span>
                                                    {row.done && (
                                                        <span className="mt-1 block italic text-slate-500">
                                                            Schon erfasst: {row.done}
                                                        </span>
                                                    )}
                                                    {byHand && row.invoice && !row.split && (
                                                        <span className="mt-1 block font-medium text-sky-700">
                                                            Von Hand zugeordnet: {invoiceLabel(row.invoice)}
                                                        </span>
                                                    )}
                                                    {row.blocked && !row.split && (
                                                        <span className="mt-1 flex items-start gap-1 text-slate-500">
                                                            <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                                            {row.blocked}
                                                        </span>
                                                    )}
                                                    {row.warnings.map((warning) => (
                                                        <span key={warning} className="mt-1 block text-amber-700">
                                                            {warning}
                                                        </span>
                                                    ))}
                                                    {row.result && (
                                                        <span
                                                            className={`mt-1 flex items-start gap-1 font-medium ${
                                                                row.result.status === 'booked'
                                                                    ? 'text-emerald-700'
                                                                    : row.result.status === 'duplicate'
                                                                      ? 'text-slate-600'
                                                                      : row.result.status === 'booking'
                                                                        ? 'text-slate-500'
                                                                        : 'text-red-600'
                                                            }`}
                                                        >
                                                            {row.result.status === 'booking' ? (
                                                                <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" />
                                                            ) : row.result.status === 'booked' ||
                                                              row.result.status === 'duplicate' ? (
                                                                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                                            ) : (
                                                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                                            )}
                                                            {row.result.message}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                            {row.split && row.splitEval && (
                                                <tr>
                                                    <td />
                                                    <td colSpan={7} className="px-2 pb-3">
                                                        <SplitEditor
                                                            rowKey={key}
                                                            transaction={row.transaction}
                                                            drafts={row.split}
                                                            evaluation={row.splitEval}
                                                            invoicesById={invoicesById}
                                                            customerNameOf={customerNameOf}
                                                            disabled={booking || handledNow(row)}
                                                            onChange={(drafts) => setSplitDrafts(key, drafts)}
                                                            onAddPart={() => addSplitPart(row)}
                                                            onCancel={() => cancelSplit(key)}
                                                            searchPartKey={
                                                                search?.rowKey === key ? (search.partKey ?? null) : null
                                                            }
                                                            onOpenSearch={(partKey) => openSearch({ rowKey: key, partKey })}
                                                            searchNode={
                                                                search?.rowKey === key && search.partKey
                                                                    ? searchNodeFor({ rowKey: key, partKey: search.partKey })
                                                                    : null
                                                            }
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {rows.length > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-3">
                        <div className="text-xs text-slate-500">
                            {bookable.length} von {visibleRows.length} angezeigten Umsätzen buchbar ·{' '}
                            {chosen.length} ausgewählt
                            {booking && ` · ${progress.done} von ${progress.total} verarbeitet`}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                variant="secondary"
                                onClick={selectHighConfidence}
                                disabled={booking}
                                className="!px-3 !py-1 !text-xs"
                            >
                                Alle mit hoher Konfidenz auswählen
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => setSelected({})}
                                disabled={booking || chosen.length === 0}
                                className="!px-3 !py-1 !text-xs"
                            >
                                Auswahl aufheben
                            </Button>
                            <Button
                                onClick={() => void handleBook()}
                                disabled={booking || chosen.length === 0}
                                className="!px-3 !py-1 !text-xs"
                            >
                                {booking ? (
                                    <>
                                        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                                        Wird gebucht …
                                    </>
                                ) : (
                                    `Ausgewählte buchen (${chosen.length})`
                                )}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
